import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PaymentMethod, PaymentProcessor } from 'models/ChargeIntent'
import { PaymentDetails, PaymentFinalizationData } from 'models/PaymentDetails'

export const initialState: PaymentDetails = {
  amount: 0,
  currency: '',
  method: null,
  type: PaymentMethod.CC,
  processor: PaymentProcessor.STRIPE,
  dcf: false,
  isOrderAnonymous: false,
  isOrderPrivate: false,
  optIn: {
    email: {
      canContact: undefined,
      canContactText: "It's ok to contact me in the future.",
    },
    sms: {
      canContact: undefined,
      canContactText:
        "It's ok to send me text messages in the future. Message and data rates may apply.",
    },
  },
  isAmex: false,
}

// Define the `paymentDetails` slice
export const paymentDetailsSlice = createSlice({
  name: 'paymentDetails',
  initialState,
  reducers: {
    // Reducer to set the payment amount
    setAmount: (state, action: PayloadAction<number>) => {
      state.amount = action.payload
    },
    // Reducer to set the payment currency
    setCurrency: (state, action: PayloadAction<string>) => {
      state.currency = action.payload
    },
    // Reducer to set the DCF flag
    setDcf: (state, action: PayloadAction<boolean>) => {
      state.dcf = action.payload
    },
    setPaymentType: (state, action: PayloadAction<PaymentMethod>) => {
      state.type = action.payload
    },
    setPaymentProcessor: (state, action: PayloadAction<PaymentProcessor>) => {
      state.processor = action.payload
    },
    // Reducer to set the payment method
    setPaymentMethod: (state, action: PayloadAction<PaymentFinalizationData>) => {
      state.method = action.payload
    },
    setIsOrderAnonymous: (state, action: PayloadAction<boolean>) => {
      state.isOrderAnonymous = action.payload
    },
    setIsOrderPrivate: (state, action: PayloadAction<boolean>) => {
      state.isOrderPrivate = action.payload
    },
    setCanContactEmail: (state, action: PayloadAction<boolean>) => {
      state.optIn.email.canContact = action.payload
    },
    setCanContactSMS: (state, action: PayloadAction<boolean>) => {
      state.optIn.sms.canContact = action.payload
    },
    setCanContactEmailText: (state, action: PayloadAction<string>) => {
      state.optIn.email.canContactText = action.payload
    },
    setCanContactSMSText: (state, action: PayloadAction<string>) => {
      state.optIn.sms.canContactText = action.payload
    },
    setIsAmex: (state, action: PayloadAction<boolean>) => {
      state.isAmex = action.payload
    },
    // Reducer to update the payment method
    updatePaymentMethod: (state, action: PayloadAction<Partial<PaymentFinalizationData>>) => {
      if (state.method) {
        state.method = { ...state.method, ...action.payload } as PaymentFinalizationData
      }
    },
  },
})

export const {
  setAmount,
  setCurrency,
  setDcf,
  setPaymentType,
  setPaymentMethod,
  setPaymentProcessor,
  setIsOrderAnonymous,
  setIsOrderPrivate,
  setCanContactEmail,
  setCanContactEmailText,
  setCanContactSMS,
  setCanContactSMSText,
  setIsAmex,
  updatePaymentMethod,
} = paymentDetailsSlice.actions

// Export the `paymentDetails` reducer
export default paymentDetailsSlice.reducer
